import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';

/**
 * Reusable pagination controls component
 * Uses semantic styling and configuration support
 * 
 * Supports two interfaces:
 * 1. Offset-based: offset, limit, total, onPageChange(offset)
 * 2. Page-based: currentPage, totalPages, onPageChange(page)
 * 
 * @param {Object} props - Component props
 * @param {number} props.offset - Current offset (0-based) - for offset-based interface
 * @param {number} props.limit - Items per page
 * @param {number} props.total - Total number of items
 * @param {Function} props.onPageChange - Function to call when page changes
 * @param {number} props.currentPage - Current page (1-based) - for page-based interface
 * @param {number} props.totalPages - Total number of pages - for page-based interface
 * @param {boolean} props.isLoading - Whether data is currently loading
 * @param {boolean} props.simplified - Whether to show simplified controls (just prev/next)
 * @param {string} props.className - Additional CSS classes
 */
const PaginationControls = ({ 
  offset,
  limit = 20, 
  total = 0, 
  onPageChange, 
  currentPage,
  totalPages,
  isLoading = false,
  simplified = false,
  className = ''
}) => {
  const colors = useColors();
  const semantic = colors.semantic;

  // Detect which interface is being used
  const isPageBased = currentPage !== undefined && totalPages !== undefined;
  
  let actualOffset, actualLimit, actualTotal, actualCurrentPage, actualTotalPages;
  let wrappedOnPageChange = onPageChange;
  
  if (isPageBased) {
    // Page-based mode: convert to offset-based internally
    actualLimit = limit || 20;
    actualCurrentPage = currentPage;
    actualTotalPages = totalPages;
    actualOffset = (currentPage - 1) * actualLimit;
    // Estimate total if not provided (for display purposes)
    actualTotal = total || (totalPages * actualLimit);
    
    // Wrap onPageChange to convert offset back to page
    const originalOnPageChange = onPageChange;
    wrappedOnPageChange = (newOffset) => {
      const newPage = Math.floor(newOffset / actualLimit) + 1;
      originalOnPageChange(newPage);
    };
  } else {
    // Offset-based mode (existing behavior)
    actualOffset = offset || 0;
    actualLimit = limit;
    actualTotal = total;
    actualCurrentPage = Math.floor(actualOffset / actualLimit) + 1;
    actualTotalPages = Math.ceil(actualTotal / actualLimit);
  }
  
  // Calculate if we can go to previous/next pages
  const hasPrevious = actualOffset > 0;
  const hasNext = actualOffset + actualLimit < actualTotal;
  
  // Handle page change
  const handlePageChange = (newOffset) => {
    if (isLoading) return;
    if (newOffset < 0) newOffset = 0;
    if (newOffset >= actualTotal) newOffset = Math.max(0, Math.floor((actualTotal - 1) / actualLimit) * actualLimit);
    wrappedOnPageChange(newOffset);
  };
  
  // Go to previous page
  const goToPrevious = () => {
    handlePageChange(actualOffset - actualLimit);
  };
  
  // Go to next page
  const goToNext = () => {
    handlePageChange(actualOffset + actualLimit);
  };
  
  // Go to specific page
  const goToPage = (page) => {
    handlePageChange((page - 1) * actualLimit);
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show a subset of pages with ellipsis
      if (currentPage <= 3) {
        // Near the start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  // If there are no items, don't render pagination
  if (actualTotal === 0) {
    return null;
  }
  
  return (
    <div className={`flex items-center justify-between mt-4 ${className}`}>
      <div className={`text-sm ${semantic.contentCardText}`}>
        {simplified ? (
          <span>
            {actualTotal > 0 ? `Showing ${actualOffset + 1}-${Math.min(actualOffset + actualLimit, actualTotal)} of ${actualTotal}` : 'No items'}
          </span>
        ) : (
          <span>
            Page {actualCurrentPage} of {actualTotalPages} ({actualTotal} items)
          </span>
        )}
      </div>
      
      <div className="flex space-x-2">
        <button
          onClick={goToPrevious}
          disabled={!hasPrevious || isLoading}
          className={`px-3 py-1 rounded transition-colors ${
            hasPrevious && !isLoading
              ? `${semantic.buttonSecondary}`
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
          }`}
          aria-label="Previous page"
        >
          <span className="sr-only">Previous</span>
          <span aria-hidden="true">&laquo;</span>
        </button>
        
        {!simplified && (
          <div className="flex space-x-1">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className={`px-3 py-1 ${semantic.contentCardText}`}>...</span>
              ) : (
                <button
                  key={`page-${page}`}
                  onClick={() => goToPage(page)}
                  disabled={page === currentPage || isLoading}
                  className={`px-3 py-1 rounded transition-colors ${
                    page === currentPage
                      ? 'cta-button-primary'
                      : semantic.buttonSecondary
                  }`}
                >
                  {page}
                </button>
              )
            ))}
          </div>
        )}
        
        <button
          onClick={goToNext}
          disabled={!hasNext || isLoading}
          className={`px-3 py-1 rounded transition-colors ${
            hasNext && !isLoading
              ? semantic.buttonSecondary
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
          }`}
          aria-label="Next page"
        >
          <span className="sr-only">Next</span>
          <span aria-hidden="true">&raquo;</span>
        </button>
      </div>
    </div>
  );
};

PaginationControls.propTypes = {
  // Offset-based interface
  offset: PropTypes.number,
  limit: PropTypes.number,
  total: PropTypes.number,
  // Page-based interface
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  // Common
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  simplified: PropTypes.bool,
  className: PropTypes.string
};

export default PaginationControls;

